/* eslint camelcase: 0 */
import { FormControl } from '@mui/material'
import styled from '@emotion/styled/macro'
import { transientProps } from 'lib/styledUtils'
import theme from 'theme'

const ITEM_HEIGHT = 32
const ITEM_PADDING_TOP = 8

export const LibraryDataSourceFormControl = styled(FormControl, transientProps('isSort'))<{
  noborder?: string
  isselected?: string
  iscustom?: string
  isSort?: boolean
  minwidth?: string
  is_opened: string
  is_disabled?: string
}>`
  border: 1px solid ${theme.colors.borderMedium};
  background: transparent;
  outline: none;
  min-width: 130px !important;
  height: 48px;
  border-radius: ${theme.attributes.borderRadius.tertiary};

  html[data-theme='light'] & {
    background: ${theme.colors.surfaceContainerBackground};
  }

  ${({ minwidth }) =>
    minwidth &&
    `
      min-width: ${minwidth};
  `};

  .MuiOutlinedInput-root {
    min-width: 130px;
    height: 48px;
    background: transparent;
    border: 0;
    color: ${theme.colors.white};
    text-align: left;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
    text-transform: capitalize;
    padding: 14px ${theme.spacing.small}px;
    cursor: pointer;

    /* Muted prefix label rendered inside the box ("Data Sources:"), shown before the
       integration logos and the highlighted value. */
    .inline-filter-label {
      color: ${theme.colors.dropdownLabel};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      text-transform: none;
      margin-right: ${theme.spacing.xSmall}px;
      white-space: nowrap;

      html[data-theme='light'] & {
        color: ${theme.colors.textCaption};
      }
    }

    .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input {
      color: ${theme.colors.cta};
      text-transform: none;

      ${({ isselected }) =>
        isselected === 'true'
          ? `
          color: ${theme.colors.cta} !important;
          
            .MuiOutlinedInput-root {
              .MuiSelect-select {
                color: ${theme.colors.cta} !important;
              }
            }
          `
          : `
            .MuiOutlinedInput-input {
              color: ${theme.colors.cta} !important;
            }
        `};

      > * {
        border: none !important;
        outline: none !important;
      }

      .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }
    }

    .MuiSelect-nativeInput {
      cursor: pointer;
    }

    svg {
      transition: all 0.5s ease-in-out;
      cursor: pointer;

      path {
        stroke: ${theme.colors.dropdownLabel};
      }
    }

    fieldset {
      outline: 0;
      border: 0;
    }

    &:hover {
      .MuiOutlinedInput-root {
        color: ${theme.colors.cta};
      }

      svg {
        path {
          stroke: ${theme.colors.cta} !important;
        }
      }
    }

    ${({ isselected }) =>
      isselected &&
      isselected === 'true' &&
      `
    color: ${theme.colors.cta} !important;
    border-color: ${theme.colors.cta} !important;
    
    .MuiOutlinedInput-root {
      border-color: ${theme.colors.cta} !important;
    }
  `};

    ${({ is_disabled }) =>
      is_disabled &&
      is_disabled === 'true' &&
      `
        border-color: ${theme.colors.dropdownBorder};
        opacity: 0.38 !important;
    `}

    &.Mui-disabled {
      opacity: 1 !important;
      border: 0;

      .MuiSelect-select {
        border: 0;
        -webkit-text-fill-color: ${theme.colors.cta} !important;
      }

      .MuiInputAdornment-root {
        img {
          border: 0;
        }
      }

      fieldset {
        border: 0;
      }
    }
  }

  ${({ is_opened }) =>
    is_opened &&
    is_opened === 'true' &&
    `
      border-color: ${theme.colors.cta} !important;
      outline: 0;
      
      .MuiOutlinedInput-root {
        color: ${theme.colors.cta};
      }
      
      svg {
        path {
          stroke: ${theme.colors.cta} !important;
        }
      }
  `};
`

export const CategoryMenuProps = {
  PaperProps: {
    sx: {
      marginTop: `${theme.spacing.xSmall}px`,
      background: theme.colors.menuBg,
      boxShadow: theme.attributes.boxShadow.navigation,
      borderRadius: theme.attributes.borderRadius.tertiary,
      padding: 0,
      minWidth: '321px !important',
      maxHeight: '600px !important',

      '.MuiList-root': {
        padding: 0,
        maxHeight: '248px'
      },

      '.MuiMenuItem-root': {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: '167px',
        height: '48px',
        padding: `${theme.spacing.sm}px ${theme.spacing.small}px`,

        '.menu-inner': {
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        },

        '&:first-of-type': {
          paddingTop: '16px'
        },

        '&:last-of-type': {
          paddingBottom: '16px'
        },

        img: {
          marginRight: `${theme.spacing.xSmall}px`
        },

        '&:hover': {
          background: `${theme.colors.menuItemHover} !important`,

          '.MuiListItemText-root .MuiTypography-root': {
            color: `${theme.colors.cta}`
          }
        },

        '.MuiCheckbox-root': {
          padding: `${theme.spacing.xSmall}px`,

          '&:hover': {
            background: 'rgba(202, 196, 208, 0.12)'
          }
        },

        '.MuiListItemText-root .MuiTypography-root': {
          fontFamily: theme.typography.fontFamily.primary,
          fontWeight: theme.typography.fontWeight.bold,
          fontSize: theme.typography.fontSize.large,
          color: theme.colors.foreground,
          lineHeight: '22px',
          fontStyle: 'normal',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden'
        },

        '.sources-count-text': {
          fontFamily: theme.typography.fontFamily.primary,
          fontWeight: theme.typography.fontWeight.bold,
          fontSize: theme.typography.fontSize.large,
          color: theme.colors.foreground,
          lineHeight: '22px',
          fontStyle: 'normal',
          textTransform: 'capitalize',
          marginLeft: `${theme.spacing.xSmall}px`
        },

        '.image-wrapper': {
          display: 'flex',

          '> img': {
            width: '18px',
            height: '18px',
            boxShadow: theme.attributes.boxShadow.chipCircle,
            margin: '-4.5px',
            borderRadius: theme.attributes.borderRadius.circle,

            '&:nth-of-type(1)': {
              zIndex: 10
            },
            '&:nth-of-type(2)': {
              zIndex: 9
            },
            '&:nth-of-type(3)': {
              zIndex: 8
            },
            '&:nth-of-type(4)': {
              zIndex: 7
            },
            '&:nth-of-type(5)': {
              zIndex: 6
            },
            '&:nth-of-type(6)': {
              zIndex: 5
            },
            '&:nth-of-type(7)': {
              zIndex: 4
            }
          }
        },

        '.text-wrapper': {
          marginRight: `${theme.spacing.small}px`
        },

        '&.Mui-selected': {
          background: `${theme.colors.legacySurfaceContainerBackground2}`
        },

        '&[aria-selected="true"]': {
          '.MuiListItemText-root .MuiTypography-root': {
            color: `${theme.colors.cta}`
          },

          '.sources-count-text': {
            color: `${theme.colors.cta}`
          }
        }
      },

      '&::-webkit-scrollbar-track': {
        background: theme.baseColors.surface[50]
      },

      '&::-webkit-scrollbar-thumb': {
        background: theme.colors.cta,
        height: '4px',
        width: '4px',
        border: '4px solid transparent',
        backgroundClip: 'content-box'
      },

      "html[data-theme='light'] &": {
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },

        '&::-webkit-scrollbar-thumb': {
          background: theme.colors.borderMedium,
          backgroundClip: 'content-box'
        }
      }
    },
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP
    }
  },
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  },
  variant: 'menu' as const
}
